import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, 
  Users, 
  Building2, 
  Truck, 
  Package, 
  DollarSign,
  Key,
  Trash2,
  Eye,
  LogIn,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Lock,
  Unlock,
  BarChart3,
  MessageSquare,
  TrendingUp,
  Calendar,
  Sun,
  Moon,
  Activity,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SUPER_ADMIN_PASSWORD = 'Abdi1998@';

// Palette used for charts across both light + dark themes
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Dark/light theme — persisted in localStorage.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('super_admin_theme') || 'dark'; } catch (_) { return 'dark'; }
  });
  const isDark = theme === 'dark';
  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    try { localStorage.setItem('super_admin_theme', next); } catch (_) {}
  };

  // Theme-aware class helpers so we don't repeat the same slate-800/white switch everywhere.
  const t = {
    page: isDark ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-900',
    card: isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-900',
    cardSoft: isDark ? 'bg-slate-700/50 text-slate-100' : 'bg-slate-100 text-slate-800',
    muted: isDark ? 'text-slate-400' : 'text-slate-500',
    heading: isDark ? 'text-white' : 'text-slate-900',
    tabList: isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
    tabActive: isDark ? 'data-[state=active]:bg-slate-700' : 'data-[state=active]:bg-blue-100 data-[state=active]:text-blue-900',
    input: isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900',
    row: isDark ? 'hover:bg-slate-700/50 border-slate-700' : 'hover:bg-slate-50 border-slate-200',
  };

  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  
  // Dialog states
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [viewCompanyDialog, setViewCompanyDialog] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);

  // User activity dialog
  const [activityDialog, setActivityDialog] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  // Contact messages
  const [contactMessages, setContactMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === SUPER_ADMIN_PASSWORD) {
      setAuthenticated(true);
      toast.success('Super Admin access granted');
      loadAllData();
    } else {
      toast.error('Invalid password');
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, companiesRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/super-admin/stats`),
        axios.get(`${BACKEND_URL}/api/super-admin/users`),
        axios.get(`${BACKEND_URL}/api/super-admin/companies`),
        axios.get(`${BACKEND_URL}/api/super-admin/analytics`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/super-admin/reset-user-password`, {
        user_id: selectedUser.id,
        new_password: newPassword
      });
      toast.success(`Password reset for ${selectedUser.email}`);
      setResetPasswordDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/super-admin/users/${selectedUser.id}`);
      toast.success(`User ${selectedUser.email} deleted`);
      setDeleteDialog(false);
      setSelectedUser(null);
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/super-admin/impersonate`, {
        user_id: user.id
      });
      // Save the token AND the user record — AuthContext reads BOTH keys on boot
      // and would redirect to /login if `user` is missing. Mark this as an
      // impersonated session so we can show a banner + Return to Admin button.
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('impersonating', JSON.stringify({
        user_id: user.id,
        user_email: user.email,
        user_name: user.name,
        started_at: new Date().toISOString(),
      }));
      toast.success(`Now logged in as ${user.email}`);
      // Use replace so the back button doesn't bring us back to the super-admin page mid-session.
      window.location.replace('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  // Load activity stats (loads this week, gross, trends) for a user.
  const handleViewActivity = async (user) => {
    setSelectedUser(user);
    setActivityLoading(true);
    setActivityDialog(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/users/${user.id}/activity`);
      setActivityData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load activity');
    } finally {
      setActivityLoading(false);
    }
  };

  const loadContactMessages = async () => {
    setMessagesLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/contact-messages`);
      setContactMessages(res.data || []);
    } catch (error) {
      toast.error('Failed to load contact messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const viewCompany = async (companyId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/companies/${companyId}`);
      setCompanyDetails(res.data);
      setViewCompanyDialog(true);
    } catch (error) {
      toast.error('Failed to load company details');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = selectedCompany === 'all' || user.company_id === selectedCompany;
    return matchesSearch && matchesCompany;
  });

  // Login Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-700 bg-slate-800">
          <CardHeader className="text-center">
            <div className="mx-auto bg-red-600 p-3 rounded-xl w-fit mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Super Admin Access</CardTitle>
            <CardDescription className="text-slate-400">
              Enter the master password to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Master Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter master password"
                  className="bg-slate-700 border-slate-600 text-white"
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
                <Lock className="h-4 w-4 mr-2" />
                Access Dashboard
              </Button>
            </form>
            <p className="text-xs text-slate-500 text-center mt-4">
              This area is restricted to system administrators only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className={`min-h-screen ${t.page} ${isDark ? 'sa-dark' : 'sa-light'}`}>
      {/* Scoped light-mode overrides so we don't have to rewrite every className. */}
      <style>{`
        .sa-light { color: #0f172a; }
        .sa-light .bg-slate-900 { background-color: #f8fafc !important; }
        .sa-light .bg-slate-800 { background-color: #ffffff !important; color: #0f172a !important; }
        .sa-light .bg-slate-700, .sa-light .bg-slate-700\\/50, .sa-light .bg-slate-700\\/40 { background-color: #f1f5f9 !important; color: #0f172a !important; }
        .sa-light .border-slate-700 { border-color: #e2e8f0 !important; }
        .sa-light .border-slate-600 { border-color: #cbd5e1 !important; }
        .sa-light .text-slate-400 { color: #475569 !important; }
        .sa-light .text-slate-500 { color: #64748b !important; }
        .sa-light .text-slate-300 { color: #1e293b !important; }
        .sa-light .text-slate-100 { color: #0f172a !important; }
        .sa-light .text-white { color: #0f172a !important; }
        /* Keep white on red destructive buttons */
        .sa-light button.bg-red-600, .sa-light button.bg-red-600 * { color: #ffffff !important; }
        .sa-light button.bg-destructive, .sa-light button.bg-destructive * { color: #ffffff !important; }
        /* Dialog content panels */
        .sa-light [data-slot="dialog-content"], .sa-light [role="dialog"] > div[class*="bg-slate"] { background-color: #ffffff !important; color: #0f172a !important; }
        .sa-light input, .sa-light textarea, .sa-light select { color: #0f172a !important; background-color: #ffffff !important; }
        /* Table rows alternation / hover */
        .sa-light table tbody tr:hover { background-color: #f1f5f9 !important; }
        .sa-light table th { color: #475569 !important; }
        /* Tabs active state must stand out */
        .sa-light [data-state="active"][role="tab"] { background-color: #dbeafe !important; color: #1e40af !important; }
        /* Ensure Recharts text (axis labels, tooltip) is dark on light */
        .sa-light .recharts-cartesian-axis-tick text { fill: #475569 !important; }
        .sa-light .recharts-legend-item-text { color: #0f172a !important; }
        /* Badges with mode-agnostic contrast */
        .sa-light .bg-blue-500\\/20 { background-color: #dbeafe !important; }
        .sa-light .text-blue-400 { color: #2563eb !important; }
        .sa-light .text-emerald-400 { color: #059669 !important; }
        .sa-light .text-amber-400 { color: #d97706 !important; }
        .sa-light .text-violet-400 { color: #7c3aed !important; }
        .sa-light .text-red-400 { color: #dc2626 !important; }
        .sa-light .text-emerald-300 { color: #059669 !important; }
        .sa-light .text-blue-300 { color: #2563eb !important; }
        /* Icon color bubbles stay colorful */
        .sa-light .bg-blue-500\\/20, .sa-light .bg-emerald-500\\/20, .sa-light .bg-amber-600\\/20, .sa-light .bg-violet-600\\/20, .sa-light .bg-red-500\\/20 { opacity: 1 !important; }
      `}</style>

      {/* Header */}
      <header className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border-b px-6 py-4`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="bg-red-600 p-2 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className={`text-xl font-bold ${t.heading}`}>Super Admin Dashboard</h1>
              <p className={`text-sm ${t.muted}`}>My Dispatch System Administration</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className={isDark ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}
              data-testid="theme-toggle-btn"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadAllData}
              disabled={loading}
              className={isDark ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setAuthenticated(false)}>
              <Lock className="h-4 w-4 mr-2" />
              Lock
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-600/20 p-2 rounded-lg">
                  <Users className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                  <p className="text-sm text-slate-400">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-600/20 p-2 rounded-lg">
                  <Building2 className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_companies || 0}</p>
                  <p className="text-sm text-slate-400">Companies</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-amber-600/20 p-2 rounded-lg">
                  <Package className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_loads || 0}</p>
                  <p className="text-sm text-slate-400">Total Loads</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-violet-600/20 p-2 rounded-lg">
                  <DollarSign className="h-5 w-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">${((stats?.total_revenue || 0) / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-slate-400">Total Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="users" className="data-[state=active]:bg-slate-700">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-slate-700">
              <Building2 className="h-4 w-4 mr-2" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-slate-700">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-slate-700" data-testid="tab-messages">
              <MessageSquare className="h-4 w-4 mr-2" />
              Messages
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle>User Management</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 bg-slate-700 border-slate-600 w-64"
                      />
                    </div>
                    <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                      <SelectTrigger className="w-48 bg-slate-700 border-slate-600">
                        <SelectValue placeholder="Filter by company" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Companies</SelectItem>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">User</TableHead>
                      <TableHead className="text-slate-400">Company</TableHead>
                      <TableHead className="text-slate-400">Role</TableHead>
                      <TableHead className="text-slate-400">Created</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(user => (
                      <TableRow key={user.id} className="border-slate-700">
                        <TableCell>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {companies.find(c => c.id === user.company_id)?.name || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedUser(user); setResetPasswordDialog(true); }}
                              title="Reset Password"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewActivity(user)}
                              title="View Activity"
                              data-testid={`view-activity-${user.id}`}
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleImpersonate(user)}
                              title="Login as User"
                              data-testid={`impersonate-${user.id}`}
                            >
                              <LogIn className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedUser(user); setDeleteDialog(true); }}
                              className="text-red-400 hover:text-red-300"
                              title="Delete User"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredUsers.length === 0 && (
                  <p className="text-center text-slate-400 py-8">No users found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle>Company Management</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Company</TableHead>
                      <TableHead className="text-slate-400">Users</TableHead>
                      <TableHead className="text-slate-400">Units</TableHead>
                      <TableHead className="text-slate-400">Loads</TableHead>
                      <TableHead className="text-slate-400">Revenue</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.map(company => (
                      <TableRow key={company.id} className="border-slate-700">
                        <TableCell>
                          <div>
                            <p className="font-medium">{company.name}</p>
                            <p className="text-sm text-slate-400">{company.email || 'No email'}</p>
                          </div>
                        </TableCell>
                        <TableCell>{company.user_count || 0}</TableCell>
                        <TableCell>{company.unit_count || 0}</TableCell>
                        <TableCell>{company.load_count || 0}</TableCell>
                        <TableCell>${(company.total_revenue || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewCompany(company.id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {companies.length === 0 && (
                  <p className="text-center text-slate-400 py-8">No companies found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab — rich charts + top companies */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Row 1: KPI chips */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Activity className="h-5 w-5 text-emerald-400" />
                      <div>
                        <p className="text-xs text-slate-400">Active Companies (30d)</p>
                        <p className="text-xl font-bold">{analytics?.active_companies_30d ?? '—'} <span className="text-sm text-slate-400">/ {analytics?.total_companies ?? '—'}</span></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-5 w-5 text-emerald-400" />
                      <div>
                        <p className="text-xs text-slate-400">New Users (7d)</p>
                        <p className="text-xl font-bold">{stats?.new_users_week || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-blue-400" />
                      <div>
                        <p className="text-xs text-slate-400">New Companies (7d)</p>
                        <p className="text-xl font-bold">{stats?.new_companies_week || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Zap className="h-5 w-5 text-amber-400" />
                      <div>
                        <p className="text-xs text-slate-400">New Loads (7d)</p>
                        <p className="text-xl font-bold">{stats?.new_loads_week || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Row 2: Revenue trend (12 weeks) */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-400" />
                    Gross Revenue by Week — Last 12 Weeks (All Companies)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.revenue_by_week?.length ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={analytics.revenue_by_week}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                        <XAxis dataKey="week_start" stroke={isDark ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} />
                        <YAxis stroke={isDark ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <RechartsTooltip
                          contentStyle={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, color: isDark ? '#f1f5f9' : '#0f172a' }}
                          formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Gross']}
                        />
                        <Line type="monotone" dataKey="gross" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-slate-400 py-8 text-center">No load data in the last 12 weeks.</p>
                  )}
                </CardContent>
              </Card>

              {/* Row 3: Top companies + User role pie */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-blue-400" />
                      Top 10 Companies by Gross Revenue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.top_companies?.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700">
                            <TableHead className="text-slate-400">#</TableHead>
                            <TableHead className="text-slate-400">Company</TableHead>
                            <TableHead className="text-slate-400 text-right">Units</TableHead>
                            <TableHead className="text-slate-400 text-right">Drivers</TableHead>
                            <TableHead className="text-slate-400 text-right">Loads</TableHead>
                            <TableHead className="text-slate-400 text-right">Gross</TableHead>
                            <TableHead className="text-slate-400 text-right">Net</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analytics.top_companies.map((c, i) => (
                            <TableRow key={c.company_id} className="border-slate-700">
                              <TableCell className="text-slate-400">{i + 1}</TableCell>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell className="text-right">{c.units}</TableCell>
                              <TableCell className="text-right">{c.drivers}</TableCell>
                              <TableCell className="text-right">{c.loads.toLocaleString()}</TableCell>
                              <TableCell className="text-right text-emerald-400 font-mono">${c.gross.toLocaleString()}</TableCell>
                              <TableCell className="text-right text-blue-300 font-mono">${c.net.toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-sm text-slate-400 py-8 text-center">No companies with revenue yet.</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-violet-400" />
                      Users by Role
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.user_role_breakdown && Object.keys(analytics.user_role_breakdown).length ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie
                            data={Object.entries(analytics.user_role_breakdown).map(([k, v]) => ({ name: k, value: v }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={80}
                            label={({ name, value }) => `${name}: ${value}`}
                            labelLine={false}
                          >
                            {Object.keys(analytics.user_role_breakdown).map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, color: isDark ? '#f1f5f9' : '#0f172a' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-400 py-8 text-center">No user data.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Row 4: Deductions-by-type bar + Signup trend */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-red-400" />
                      Top Deduction Categories (All Companies)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.deductions_by_type?.length ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={analytics.deductions_by_type} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                          <XAxis type="number" stroke={isDark ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                          <YAxis dataKey="name" type="category" stroke={isDark ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} width={100} />
                          <RechartsTooltip
                            contentStyle={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, color: isDark ? '#f1f5f9' : '#0f172a' }}
                            formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Total']}
                          />
                          <Bar dataKey="total" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-400 py-8 text-center">No deductions recorded.</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-amber-400" />
                      User Signups — Last 30 Days
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.signup_trend_30d?.length ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={analytics.signup_trend_30d}>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                          <XAxis dataKey="date" stroke={isDark ? '#94a3b8' : '#64748b'} tick={{ fontSize: 10 }} />
                          <YAxis stroke={isDark ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} allowDecimals={false} />
                          <RechartsTooltip
                            contentStyle={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, color: isDark ? '#f1f5f9' : '#0f172a' }}
                          />
                          <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-400 py-8 text-center">No signups in the last 30 days.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Row 5: Legacy summary cards kept for parity */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-blue-400" />
                      System Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Total Units</span>
                      <span className="font-bold">{stats?.total_units || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Total Drivers</span>
                      <span className="font-bold">{stats?.total_drivers || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Total Deductions</span>
                      <span className="font-bold">${(stats?.total_deductions || 0).toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-emerald-400" />
                      Weekly Deltas
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">New Users (This Week)</span>
                      <span className="font-bold text-emerald-400">{stats?.new_users_week || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">New Companies (This Week)</span>
                      <span className="font-bold text-emerald-400">{stats?.new_companies_week || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Loads Created (This Week)</span>
                      <span className="font-bold text-emerald-400">{stats?.new_loads_week || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Contact Messages Tab — guaranteed inbox even if email delivery fails */}
          <TabsContent value="messages">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-blue-400" />
                    Contact Form Submissions
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Every message users send through the public Contact form, even if the email delivery fails.
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadContactMessages}
                  className="bg-slate-700 border-slate-600 hover:bg-slate-600"
                  data-testid="refresh-messages-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${messagesLoading ? 'animate-spin' : ''}`} />
                  Load / Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {contactMessages.length === 0 && !messagesLoading ? (
                  <p className="text-center text-slate-400 py-8">
                    Click &quot;Load / Refresh&quot; above to fetch messages.
                  </p>
                ) : messagesLoading ? (
                  <p className="text-center text-slate-400 py-8">Loading…</p>
                ) : (
                  <div className="space-y-3" data-testid="contact-messages-list">
                    {contactMessages.map((m) => (
                      <div key={m.id} className="bg-slate-700/40 border border-slate-700 rounded-lg p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-white truncate">{m.subject}</p>
                            <p className="text-xs text-slate-400 truncate">
                              From <span className="text-blue-300">{m.name}</span>
                              {m.company ? <> · <span className="text-amber-300">{m.company}</span></> : null}
                              {' · '}<a href={`mailto:${m.email}`} className="text-blue-400 underline">{m.email}</a>
                              {m.phone ? <> · {m.phone}</> : null}
                            </p>
                          </div>
                          <div className="text-xs text-slate-400 flex-shrink-0 flex items-center gap-2">
                            <Badge variant={m.status === 'sent' ? 'default' : 'destructive'}>
                              {m.status || 'sent'}
                            </Badge>
                            <span>{new Date(m.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <p className="text-sm text-slate-200 whitespace-pre-wrap">{m.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} className="bg-blue-600 hover:bg-blue-700">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Delete User
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete {selectedUser?.email}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Company Dialog */}
      <Dialog open={viewCompanyDialog} onOpenChange={setViewCompanyDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{companyDetails?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Company Details and Statistics
            </DialogDescription>
          </DialogHeader>
          {companyDetails && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Email</p>
                  <p className="font-medium">{companyDetails.email || 'Not set'}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Phone</p>
                  <p className="font-medium">{companyDetails.phone || 'Not set'}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">MC Number</p>
                  <p className="font-medium">{companyDetails.mc_number || 'Not set'}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">DOT Number</p>
                  <p className="font-medium">{companyDetails.dot_number || 'Not set'}</p>
                </div>
              </div>
              <div className="border-t border-slate-700 pt-4">
                <h4 className="font-medium mb-3">Company Users</h4>
                <div className="space-y-2">
                  {companyDetails.users?.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-2 bg-slate-700/30 rounded">
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-slate-400">{user.email}</p>
                      </div>
                      <Badge>{user.role}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewCompanyDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Activity Dialog */}
      <Dialog open={activityDialog} onOpenChange={setActivityDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-activity-dialog">
          <DialogHeader>
            <DialogTitle>User Activity{selectedUser ? ` — ${selectedUser.name}` : ''}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Weekly stats and 6-week trend for this user's company
            </DialogDescription>
          </DialogHeader>
          {activityLoading ? (
            <div className="py-10 text-center text-slate-400">
              <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin" />
              Loading activity…
            </div>
          ) : activityData ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Loads This Week</p>
                  <p className="text-2xl font-bold mt-1">{activityData.company_loads_this_week}</p>
                  <p className="text-[10px] text-slate-500 mt-1">by this user: {activityData.user_loads_this_week}</p>
                </div>
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Gross This Week</p>
                  <p className="text-2xl font-bold mt-1">${activityData.gross_this_week.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                </div>
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Units</p>
                  <p className="text-2xl font-bold mt-1">{activityData.units_count}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{activityData.active_units} active</p>
                </div>
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Drivers</p>
                  <p className="text-2xl font-bold mt-1">{activityData.drivers_count}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">6-Week Trend</p>
                <div className="space-y-1.5">
                  {activityData.trends.map((t) => {
                    const max = Math.max(1, ...activityData.trends.map((x) => x.gross));
                    const pct = Math.round((t.gross / max) * 100);
                    return (
                      <div key={t.week_start} className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-24 flex-shrink-0">{t.week_start}</span>
                        <div className="flex-1 bg-slate-800 rounded h-5 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-300 w-28 text-right">
                          ${t.gross.toLocaleString(undefined, { maximumFractionDigits: 0 })} · {t.loads} loads
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                onClick={() => handleImpersonate(selectedUser)}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="activity-impersonate-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login as {selectedUser?.name}
              </Button>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
